import { useState, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { PowerSchedule } from '@/features/plants/schemas/schedule-plant.schema.ts';

interface ActivateScheduleModalProps {
  children: ReactNode;
  currentSchedule: PowerSchedule | undefined;
  initialSchedules: PowerSchedule[];
  schedules: PowerSchedule[];
  onActivateSchedule: (scheduleId: string, activate: boolean) => void;
}

export function ActivateScheduleModal({
  children,
  currentSchedule,
  initialSchedules,
  schedules,
  onActivateSchedule,
}: ActivateScheduleModalProps) {
  const [open, setOpen] = useState(false);

  const isActive = !currentSchedule?.isActive;
  const activeSchedule = schedules.find(
    (s) => s.isActive && s.scheduleId !== currentSchedule?.scheduleId
  );
  const activeInitialSchedule = initialSchedules.find(
    (s) => s.isActive && s.scheduleId !== currentSchedule?.scheduleId
  );

  const hasActiveScheduleConflict =
    (isActive && activeSchedule) || (isActive && activeInitialSchedule);

  const handleConfirm = () => {
    if (hasActiveScheduleConflict || !currentSchedule) return;

    onActivateSchedule(currentSchedule.scheduleId, isActive);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isActive ? 'Activate Schedule' : 'Deactivate Schedule'}</DialogTitle>
          <DialogDescription>
            {isActive ? (
              <>
                This will activate the schedule "{currentSchedule?.scheduleName}". The schedule
                will start running according to its configured time windows.
              </>
            ) : (
              <>
                This will deactivate the schedule "{currentSchedule?.scheduleName}". The
                schedule will stop running and no automated actions will be performed.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {hasActiveScheduleConflict && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Cannot activate this schedule. Please deactivate the "
              {activeSchedule?.scheduleName}" schedule first and then save, as only one
              schedule can be active at a time.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={Boolean(hasActiveScheduleConflict)}>
            {isActive ? 'Activate' : 'Deactivate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
